import React, { Component } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    LinearProgress,
    IconButton,
    Stack,
    Chip,
    Avatar
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';

import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend
);

class ReportDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            faturamento: this.props.data.faturamento || 0,
            pedidos: this.props.data.pedidos || 0,
            ticketMedio: this.props.data.ticketMedio || 0,
            clientes: this.props.data.clientes || 0,
            produtosMaisVendidos: this.props.data.produtosMaisVendidos || [],
            pedidosPorHora: this.props.data.pedidosPorHora || [],
            performanceDiasSemana: this.props.data.performanceDiasSemana || [],
            metodosPagamento: this.props.data.metodosPagamento || [],
            dataInicioFormatada: this.props.data.dataInicioFormatada || '',
            dataFimFormatada: this.props.data.dataFimFormatada || ''
        };
    }

    formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value || 0);
    }

    getPaymentLabel = (method) => {
        const labels = {
            debit_card: 'Cartão de Débito',
            credit_card: 'Cartão de Crédito',
            qr: 'Pix / QR Code',
            money: 'Dinheiro',
            multi: 'Múltiplo / Misto'
        };
        return labels[method] || method;
    }

    getChartData = () => {
        const { pedidosPorHora } = this.state;
        const labels = Array.from({ length: 24 }, (_, i) => `${i}h`);

        const dataPoints = labels.map(label => {
            const match = pedidosPorHora.find(p => p.hora === label || p.hora === `${label.replace('h', '')}h`);
            return match ? match.pedidos : 0;
        });

        return {
            labels,
            datasets: [
                {
                    data: dataPoints,
                    backgroundColor: '#8A3FFC',
                    borderRadius: 4,
                    borderSkipped: false,
                    barPercentage: 0.7,
                }
            ]
        };
    }

    getChartOptions = () => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (context) => ` ${context.raw} pedidos`
                    }
                }
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: {
                        color: '#9e9e9e',
                        font: { size: 10 },
                        callback: function (val, index) {
                            const targetLabels = ['00h', '03h', '06h', '09h', '12h', '15h', '18h', '21h', '23h'];
                            return targetLabels.includes(this.getLabelForValue(index)) ? this.getLabelForValue(index) : '';
                        }
                    }
                },
                y: {
                    border: { dash: [4, 4] },
                    grid: { color: '#f0f0f0' },
                    ticks: {
                        color: '#9e9e9e',
                        font: { size: 10 },
                        beginAtZero: true,
                        precision: 0
                    }
                }
            }
        };
    }

    render() {
        const {
            faturamento,
            pedidos,
            ticketMedio,
            clientes,
            produtosMaisVendidos,
            performanceDiasSemana,
            metodosPagamento,
            dataInicioFormatada,
            dataFimFormatada
        } = this.state;

        const cardStyle = {
            borderRadius: '16px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
            border: '1px solid #f0f0f0',
            height: '100%'
        };

        const totalPagamentosFaturado = metodosPagamento.reduce((acc, curr) => acc + curr.totalFaturado, 0);

        // Ordenar produtos por quantidade vendida (volume de vendas)
        const produtosOrdenados = [...produtosMaisVendidos].sort((a, b) => (b.quantidade || 0) - (a.quantidade || 0));
        const maxQuantidade = produtosOrdenados[0]?.quantidade || 1;

        return (
            <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>

                {/* VISÃO GERAL - KPIS / CARDS SUPERIORES */}
                <Grid container spacing={3} mb={3}>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ p: '20px !important' }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', width: 48, height: 48 }}>
                                        <AttachMoneyIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Faturamento Total</Typography>
                                        <Typography variant="h6" fontWeight="700" color="#2e7d32">
                                            {this.formatCurrency(faturamento)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ p: '20px !important' }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ bgcolor: '#f3e5f5', color: '#8A3FFC', width: 48, height: 48 }}>
                                        <ReceiptLongOutlinedIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Total de Pedidos</Typography>
                                        <Typography variant="h6" fontWeight="700" color="#212121">
                                            {pedidos}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ p: '20px !important' }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 48, height: 48 }}>
                                        <ShoppingBagOutlinedIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Ticket Médio</Typography>
                                        <Typography variant="h6" fontWeight="700" color="#212121">
                                            {this.formatCurrency(ticketMedio)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ p: '20px !important' }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ bgcolor: '#fff3e0', color: '#ed6c02', width: 48, height: 48 }}>
                                        <PeopleAltOutlinedIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Clientes Atendidos</Typography>
                                        <Typography variant="h6" fontWeight="700" color="#212121">
                                            {clientes}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* BLOCO PRINCIPAL: PRODUTOS & CATEGORIAS */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* PRODUTOS MAIS VENDIDOS */}
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ p: '24px !important' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Box flexGrow={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LocalOfferOutlinedIcon sx={{ color: '#8A3FFC', fontSize: 20 }} />
                                            <Typography variant="subtitle1" fontWeight="600" color="#212121">Produtos Mais Vendidos</Typography>
                                        </Stack>
                                        <Typography variant="caption" color="textSecondary">Ordenados por volume de vendas</Typography>
                                    </Box>
                                    <IconButton size="small"><KeyboardArrowUpIcon /></IconButton>
                                </Box>

                                <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
                                    {produtosOrdenados.map((prod, index) => {
                                        // Barra de progresso relativa ao produto mais vendido (o 1º da lista = 100%)
                                        const barProgress = (prod.quantidade / maxQuantidade) * 100;
                                        // Porcentagem real do produto sobre o faturamento total da loja
                                        const percentageFaturamento = faturamento > 0 ? (prod.totalFaturado / faturamento) * 100 : 0;

                                        return (
                                            <Box key={index} display="flex" alignItems="center" mt={2}>
                                                <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#f0e6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, fontWeight: 'bold', color: '#8A3FFC', fontSize: '12px', flexShrink: 0 }}>
                                                    {index + 1}
                                                </Box>
                                                <Box flexGrow={1}>
                                                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                        <Typography variant="body2" fontWeight="500">{prod.nome}</Typography>
                                                        <Typography variant="body2" fontWeight="600" color="#2e7d32">
                                                            {this.formatCurrency(prod.totalFaturado)}
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                                        <Box width="100%" mr={1}>
                                                            <LinearProgress variant="determinate" value={barProgress} sx={{ height: 6, borderRadius: 3, backgroundColor: '#f0f0f0', '& .MuiLinearProgress-bar': { backgroundColor: '#8A3FFC' } }} />
                                                        </Box>
                                                        <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                                                            {prod.quantidade} un. • {percentageFaturamento.toFixed(1)}%
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* CATEGORIAS */}
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ p: '24px !important' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Box flexGrow={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <FolderOutlinedIcon sx={{ color: '#8A3FFC', fontSize: 20 }} />
                                            <Typography variant="subtitle1" fontWeight="600" color="#212121">Categorias</Typography>
                                        </Stack>
                                        <Typography variant="caption" color="textSecondary">Distribuição geral das vendas</Typography>
                                    </Box>
                                    <IconButton size="small"><KeyboardArrowUpIcon /></IconButton>
                                </Box>

                                <Box display="flex" alignItems="center" mt={3}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#8A3FFC', mr: 2 }} />
                                    <Box flexGrow={1}>
                                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                                            <Typography variant="body2" fontWeight="500">Geral</Typography>
                                            <Typography variant="body2" fontWeight="600" color="#2e7d32">
                                                {this.formatCurrency(faturamento)}
                                            </Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Box width="100%" mr={2}>
                                                <LinearProgress variant="determinate" value={100} sx={{ height: 6, borderRadius: 3, backgroundColor: '#f0f0f0', '& .MuiLinearProgress-bar': { backgroundColor: '#8A3FFC' } }} />
                                            </Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                                                100.0%
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* BLOCO PICO DE HORÁRIO E DIAS DA SEMANA */}
                <Grid container sx={{ mt: 0.5, mb: 3 }} spacing={3} >
                    {/* HORÁRIOS DE PICO */}
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ p: '24px !important' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Box flexGrow={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <AccessTimeOutlinedIcon sx={{ color: '#8A3FFC', fontSize: 20 }} />
                                            <Typography variant="subtitle1" fontWeight="600" color="#212121">Horários de Pico</Typography>
                                        </Stack>
                                        <Typography variant="caption" color="textSecondary">Distribuição do volume de pedidos ao longo do dia</Typography>
                                    </Box>
                                    <IconButton size="small"><KeyboardArrowUpIcon /></IconButton>
                                </Box>

                                <Box sx={{ height: 180, width: '100%', pt: 1 }}>
                                    <Bar
                                        data={this.getChartData()}
                                        options={this.getChartOptions()}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* PERFORMANCE POR DIA DA SEMANA */}
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ p: '24px !important' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                                    <Box flexGrow={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CalendarMonthOutlinedIcon sx={{ color: '#8A3FFC', fontSize: 20 }} />
                                            <Typography variant="subtitle1" fontWeight="600" color="#212121">Dias da Semana</Typography>
                                        </Stack>
                                        <Typography variant="caption" color="textSecondary">Desempenho de vendas no período</Typography>
                                    </Box>
                                    <IconButton size="small"><KeyboardArrowUpIcon /></IconButton>
                                </Box>

                                <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ overflowX: 'auto', pb: 1 }}>
                                    {performanceDiasSemana.map((item, index) => (
                                        <Box key={index} sx={{
                                            flex: 1,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '12px',
                                            p: 1.5,
                                            textAlign: 'center',
                                            minWidth: '65px',
                                            backgroundColor: item.pedidos > 0 ? '#ffffff' : '#fafafa'
                                        }}>
                                            <Typography variant="caption" color="textSecondary" display="block" mb={0.5} sx={{ fontWeight: '500' }}>
                                                {item.dia.split('-')[0]}
                                            </Typography>
                                            <Typography variant="body1" fontWeight="700" color="#212121">
                                                {item.pedidos}
                                            </Typography>
                                            <Typography variant="caption" fontWeight="600" color={item.faturamento > 0 ? '#2e7d32' : 'textSecondary'}>
                                                {this.formatCurrency(item.faturamento)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* MÉTODOS DE PAGAMENTO */}
                <Grid container sx={{ mt: 0.5 }} spacing={3}>
                    <Grid item size={{ xs: 12 }}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ p: '24px !important' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Box flexGrow={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PaymentOutlinedIcon sx={{ color: '#8A3FFC', fontSize: 20 }} />
                                            <Typography variant="subtitle1" fontWeight="600" color="#212121">Métodos de Pagamento</Typography>
                                        </Stack>
                                        <Typography variant="caption" color="textSecondary">Faturamento distribuído por forma de recebimento</Typography>
                                    </Box>
                                    <IconButton size="small"><KeyboardArrowUpIcon /></IconButton>
                                </Box>

                                {metodosPagamento.map((item, index) => {
                                    const percentage = totalPagamentosFaturado > 0 ? (item.totalFaturado / totalPagamentosFaturado) * 100 : 0;
                                    return (
                                        <Box key={index} display="flex" alignItems="center" mt={2}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#8A3FFC', mr: 2 }} />
                                            <Box flexGrow={1}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="body2" fontWeight="600">
                                                            {this.getPaymentLabel(item.forma)}
                                                        </Typography>
                                                        <Chip label={`${item.quantidadePedidos} pedidos`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    </Stack>
                                                    <Typography variant="body2" fontWeight="700" color="#2e7d32">
                                                        {this.formatCurrency(item.totalFaturado)}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Box width="100%" mr={2}>
                                                        <LinearProgress variant="determinate" value={percentage} sx={{ height: 6, borderRadius: 3, backgroundColor: '#f0f0f0', '& .MuiLinearProgress-bar': { backgroundColor: '#8A3FFC' } }} />
                                                    </Box>
                                                    <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                                                        {percentage.toFixed(1)}%
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        );
    }
}

export default ReportDetails;